import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-final-form'
import { FormGroup } from './renderers'

// This component uses _address_form.html.erb as a reference for Google Maps Autocomplete
export const fieldAddressAutocomplete = (options) => {
  const {
    error,
    title,
    input,
    hint,
    disabled,
    compact,
    fullWidth,
    className,
    oneLine,
    readOnly,
    required,
    placeholder,
    optionSources,
  } = options

  const form = useForm()
  const inputRef = useRef(null)
  const autocompleteRef = useRef(null)
  const [localValue, setLocalValue] = useState(input.value || '')

  // Sync local value with React Final Form when it changes externally
  useEffect(() => {
    setLocalValue(input.value || '')
  }, [input.value])

  // Extract address data from Google Maps place object
  const extractAddressData = (place) => {
    const components = place.address_components || []

    const streetNumber = components.find(c => c.types.includes('street_number'))?.long_name || ''
    const route = components.find(c => c.types.includes('route'))?.long_name || ''
    const streetAddress = place.name || `${streetNumber} ${route}`.trim()

    const city = components.find(c => c.types.includes('locality'))?.long_name ||
      components.find(c => c.types.includes('administrative_area_level_2'))?.long_name || ''

    const zipcode = components.find(c => c.types.includes('postal_code'))?.long_name || ''
    const country = components.find(c => c.types.includes('country'))
    const state = components.find(c => c.types.includes('administrative_area_level_1'))

    return { streetAddress, city, zipcode, country, state }
  }

  // Handle state mapping
  const updateStateField = (state, countryId) => {
    const stateLongName = state.long_name
    const stateShortName = state.short_name
    const statesKey = `states_${countryId}`
    const stateOptions = optionSources[statesKey]

    if (!stateOptions) {
      form.change('state_text', stateLongName)
      return
    }

    // Try to find state by both long name and short name (case-sensitive and case-insensitive)
    const stateOption = stateOptions.find(s => 
      s.title === stateLongName || 
      s.title === stateShortName ||
      s.title.toLowerCase() === stateLongName.toLowerCase() ||
      s.title.toLowerCase() === stateShortName.toLowerCase()
    )

    if (stateOption) {
      form.change('state_id', stateOption.value)
      // Clear any previous state_text value
      form.change('state_text', '')
    } else {
      // If no match found, use the long name as text
      form.change('state_text', stateLongName)
      form.change('state_id', null)
    }
  }

  // Handle country mapping and form updates
  const updateCountryAndState = (country, state) => {
    if (!country || !optionSources?.countries) return

    const countryCode = country.short_name
    const countryOption = optionSources.countries.find(c =>
      c.iso2 === countryCode || c.iso3 === countryCode
    )

    if (!countryOption) return

    form.change('country_id', countryOption.value)

    if (state) {
      updateStateField(state, countryOption.value)
    }
  }

  // Handle place selection from Google Maps autocomplete
  const handlePlaceSelect = () => {
    const place = autocompleteRef.current.getPlace()

    if (!place.geometry) return

    const { streetAddress, city, zipcode, country, state } = extractAddressData(place)

    setLocalValue(streetAddress)

    form.batch(() => {
      input.onChange(streetAddress)
      form.change('city', city)
      form.change('zipcode', zipcode)
      updateCountryAndState(country, state)
    })
  }

  // Initialize Google Maps Autocomplete
  const initAutocomplete = () => {
    if (!window.google?.maps?.places || !inputRef.current) return

    const options = {
      fields: ["address_components", "geometry", "name", "place_id"],
      types: ['establishment', 'geocode']
    }

    autocompleteRef.current = new window.google.maps.places.Autocomplete(
      inputRef.current,
      options
    )

    autocompleteRef.current.addListener('place_changed', handlePlaceSelect)

    // Prevent form submission on enter
    inputRef.current.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
      }
    })
  }

  useEffect(() => {
    initAutocomplete()

    // Cleanup function
    return () => {
      if (autocompleteRef.current && window.google) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current)
      }
    }
  }, [])

  // Handle country changes to restrict autocomplete
  useEffect(() => {
    const countryId = form.getState().values.country_id

    if (!autocompleteRef.current || !countryId || !optionSources?.countries) return

    const country = optionSources.countries.find(c => c.value === countryId)

    if (country) {
      autocompleteRef.current.setComponentRestrictions({
        country: [country.iso2]
      })
    }
  }, [form.getState().values.country_id, optionSources])

  const handleInputChange = (e) => {
    const value = e.target.value
    setLocalValue(value)
    input.onChange(value)
  }

  return (
    <FormGroup
      error={error}
      title={title}
      hint={hint}
      compact={compact}
      fullWidth={fullWidth}
      className={className}
      oneLine={oneLine}
      required={required}
    >
      <input
        ref={inputRef}
        name={input.name}
        type="text"
        disabled={disabled}
        readOnly={readOnly}
        placeholder={placeholder || "Start typing your business address..."}
        className="form-control"
        autoComplete="off"
        value={localValue}
        onChange={handleInputChange}
        onFocus={input.onFocus}
        onBlur={input.onBlur}
      />
    </FormGroup>
  )
}
